import { useQueryIntegrationOnboardingSessions } from '@/hooks/query/integrations/general/onboarding-session/useQueryIntegrationOnboardingSessions';
import { setDatadogSectionalEditorVersion } from '@/utils/tracing';
import { COMPLETED_ONBOARDING_SESSIONS_PARAMS } from '@heidi/integrations/connected-apps/contracts';
import { useEffect } from 'react';

import { selectLatestOnboardingSession } from '@heidi/growth';
import { useResolvedEditorVersion, warnIfUnrecognized } from './useResolvedEditorVersion';

/**
 * Mounted at the app root post-auth so the React Query cache for completed
 * onboarding sessions is warm before any imperative editor determination
 * (`resolveEditorVersion` → `isEnableBlockEditorV1` → note generation, copy
 * helpers) runs — without it, the first imperative call on a cold cache
 * returns the fail-closed `html` and routes the first paint down the legacy
 * path.
 *
 * The determination is backend-authoritative (the resolved
 * `template_editor_version` on the session response), so this warms for
 * every possibly-Athena user — no flag gate. One deduped request per app
 * load on the shared cache entry (1h `staleTime`), deduped with every other
 * completed-sessions consumer. Replaces the retired
 * `AthenaJsonSectionalNotePrefetch`, which warmed the same entry but only
 * when the JSON cohort flag was on.
 *
 * This is also the SINGLE writer of the `sectional_editor_version` global
 * dimension and the SINGLE caller of `warnIfUnrecognized`. Both side effects
 * lived in the shared `useResolvedEditorVersion` hook, which five callers
 * invoke with a session-scoped provider — so an Athena user opening an
 * EMIS/Epic-linked session hit the non-authoritative branch and removed the
 * dimension globally (last-writer-wins), with no re-fire to restore it. They
 * now live here, mounted once with no provider arg (the user-level read), so
 * the dimension reflects the user's resolved editor and a session-scoped
 * consumer can't blank it. The consumer hooks go back to being pure readers.
 *
 * The raw session is read via a second subscription to the same deduped query
 * entry (no extra network) so `warnIfUnrecognized` has the document it needs
 * without the resolver exposing it.
 */
export const EditorVersionPrefetch = (): null => {
  const resolution = useResolvedEditorVersion();
  const { data: session } = useQueryIntegrationOnboardingSessions({
    params: COMPLETED_ONBOARDING_SESSIONS_PARAMS,
    select: (data) => selectLatestOnboardingSession(data.sessions, undefined),
  });

  useEffect(() => {
    // Unsettled (skip-token / in-flight / query error) — no decision yet.
    // Don't stamp a value CS would trust as a cohort, and don't warn on a
    // session that may not be the final one.
    if (!resolution.isSettled) return;

    warnIfUnrecognized(session);
    if (!resolution.isAuthoritative) {
      // No concrete decision for this user — remove the property so an
      // unstamped record stays distinguishable from a real `html` resolution.
      setDatadogSectionalEditorVersion(null);
      return;
    }
    setDatadogSectionalEditorVersion(resolution.version);
  }, [resolution.isSettled, resolution.isAuthoritative, resolution.version, session]);

  return null;
};
